import { useQuery, type UseQueryResult } from '@tanstack/react-query';
import { useCallback, useMemo } from 'react';

import { requisitar } from '@/lib/http';
import type { Referencias } from '@/types/api';

/**
 * Resolucao de id em nome, para as telas de acao.
 *
 * O contrato da API trafega id: `areaResponsavelId: 3`. A traducao para "Suporte" e do
 * cliente, e sai daqui.
 *
 * CACHE LONGO, DE PROPOSITO. Cadastro muda raramente e esta lista e consultada por toda
 * linha de uma listagem de ate 200 acoes. Sem cache compartilhado, cada tela buscaria a
 * mesma lista, e uma tabela de 200 linhas dispararia 200 resolucoes contra dados que nao
 * mudaram.
 */

/** Meia hora. Cadastro muda por operacao humana, nao por evento de sistema. */
const VALIDADE_EM_MS = 30 * 60 * 1000;

export const chaveDeReferencias = ['referencias'] as const;

function obterReferencias(sinal?: AbortSignal): Promise<Referencias> {
  return requisitar<Referencias>('/api/referencias', sinal === undefined ? {} : { sinal });
}

export function useReferencias(): UseQueryResult<Referencias> {
  return useQuery({
    queryKey: chaveDeReferencias,
    queryFn: ({ signal }) => obterReferencias(signal),
    staleTime: VALIDADE_EM_MS,
  });
}

/** Resolvedores de nome, prontos para uso em tabela. */
export interface Resolvedores {
  nomeDaArea: (id: number | null) => string;
  nomeDoCliente: (id: number | null) => string;
  nomeDoUsuario: (id: number | null) => string;
  /**
   * Endereco do work item no Azure DevOps, ou `null`.
   *
   * `null` em dois casos que a tela trata igual: acao sem work item, e integracao nao
   * configurada. Nos dois ela mostra texto em vez de link, porque link que leva a pagina de
   * erro e pior do que numero sem link.
   */
  urlDoWorkItem: (numero: number | null) => string | null;
  /** Areas ativas, para seletor. */
  areasAtivas: { id: number; nome: string }[];
  /** Clientes ativos, para seletor. */
  clientesAtivos: { id: number; nome: string }[];
  /** Usuarios ativos no escopo, para seletor de responsavel. */
  usuariosAtivos: { id: number; nome: string }[];
  carregando: boolean;
}

/**
 * Resolvedores memorizados.
 *
 * Os mapas sao construidos uma vez por resposta, e nao a cada linha: uma busca linear
 * por linha numa tabela de 200 acoes com 34 clientes faria milhares de comparacoes por
 * render.
 */
export function useResolvedores(): Resolvedores {
  const { data, isPending } = useReferencias();

  const mapas = useMemo(
    () => ({
      areas: new Map((data?.areas ?? []).map((a) => [a.id, a])),
      clientes: new Map((data?.clientes ?? []).map((c) => [c.id, c])),
      usuarios: new Map((data?.usuarios ?? []).map((u) => [u.id, u])),
    }),
    [data],
  );

  /*
    Registro inativo CONTINUA sendo exibido, com marca. Esconder o nome de uma area
    inativa faria a acao antiga voltar a mostrar id, que e o problema que esta tela
    existe para resolver.

    Id desconhecido cai em "#3" em vez de vazio: quem esta diagnosticando precisa saber
    que existe um vinculo, e para qual id.
  */
  const nomeDaArea = useCallback(
    (id: number | null) => {
      if (id === null) {
        return '—';
      }

      const area = mapas.areas.get(id);

      if (area === undefined) {
        return `#${String(id)}`;
      }

      return area.ativa ? area.nome : `${area.nome} (inativa)`;
    },
    [mapas],
  );

  const nomeDoCliente = useCallback(
    (id: number | null) => {
      if (id === null) {
        return '—';
      }

      const cliente = mapas.clientes.get(id);

      if (cliente === undefined) {
        return `#${String(id)}`;
      }

      return cliente.ativo ? cliente.nome : `${cliente.nome} (inativo)`;
    },
    [mapas],
  );

  const nomeDoUsuario = useCallback(
    (id: number | null) => {
      if (id === null) {
        return '—';
      }

      const usuario = mapas.usuarios.get(id);

      if (usuario === undefined) {
        return `#${String(id)}`;
      }

      return usuario.ativo ? usuario.nome : `${usuario.nome} (desativado)`;
    },
    [mapas],
  );

  const urlDoWorkItem = useCallback(
    (numero: number | null) => {
      const molde = data?.urlDeWorkItem;

      if (numero === null || molde === undefined || molde === null) {
        return null;
      }

      return molde.replace('{id}', String(numero));
    },
    [data],
  );

  return useMemo(
    () => ({
      nomeDaArea,
      nomeDoCliente,
      nomeDoUsuario,
      urlDoWorkItem,
      areasAtivas: (data?.areas ?? [])
        .filter((a) => a.ativa)
        .map((a) => ({ id: a.id, nome: a.nome })),
      clientesAtivos: (data?.clientes ?? [])
        .filter((c) => c.ativo)
        .map((c) => ({ id: c.id, nome: c.nome })),
      usuariosAtivos: (data?.usuarios ?? [])
        .filter((u) => u.ativo)
        .map((u) => ({ id: u.id, nome: u.nome })),
      carregando: isPending,
    }),
    [data, isPending, nomeDaArea, nomeDoCliente, nomeDoUsuario, urlDoWorkItem],
  );
}
